import React, { useState, useEffect } from 'react';
import { FaTimes, FaProjectDiagram, FaSave, FaSpinner } from 'react-icons/fa';
import { CloseModal } from '../sites/closemodal';

interface ActivityFormData {
  activity_name: string;
  activity_description: string;
  status?: 'ACTIVE' | 'SUSPENDED';
}

interface AddActivityOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ActivityFormData) => void;
  loading?: boolean;
}

export const AddActivityOffcanvas: React.FC<AddActivityOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState<ActivityFormData>({
    activity_name: '',
    activity_description: '',
    status: 'ACTIVE'
  });

  const [errors, setErrors] = useState<Partial<ActivityFormData>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ActivityFormData, boolean>>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        activity_name: '',
        activity_description: '',
        status: 'ACTIVE'
      });
      setErrors({});
      setTouched({});
    } else {
      setShowCloseModal(false);
    }
  }, [isOpen]);

  // Validation
  const validateField = (name: keyof ActivityFormData, value: string) => {
    switch (name) {
      case 'activity_name':
        if (!value.trim()) {
          return 'Activity name is required';
        }
        if (value.trim().length < 3) {
          return 'Activity name must be at least 3 characters';
        }
        if (value.trim().length > 100) {
          return 'Activity name cannot exceed 100 characters';
        }
        return '';
      case 'activity_description':
        if (value.trim().length > 500) {
          return 'Description cannot exceed 500 characters';
        }
        return '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof ActivityFormData;
      const error = validateField(fieldKey, formData[fieldKey] as string);
      if (error) {
        newErrors[fieldKey] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ActivityFormData;

    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Handle blur events for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ActivityFormData;

    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    const error = validateField(fieldName, value);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      activity_name: true,
      activity_description: true,
      status: true
    });

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        setShowCloseModal(true);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  const requestClose = () => {
    if (!loading) {
      setShowCloseModal(true);
    }
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={requestClose}
      />

      {/* Offcanvas */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add New Activity</h2>
            <p className="text-sm text-gray-600 mt-1">Please fill in the form below to add a new activity.</p>
          </div>
          <button
            onClick={requestClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Activity Name */}
            <div>
              <label htmlFor="activity_name" className="block text-sm font-medium text-gray-700 mb-2">
                Activity Name *
              </label>
              <input
                type="text"
                id="activity_name"
                name="activity_name"
                value={formData.activity_name}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.activity_name && touched.activity_name
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                  } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                placeholder="Enter activity name"
                maxLength={100}
              />
              {errors.activity_name && touched.activity_name && (
                <p className="mt-1 text-sm text-red-600">{errors.activity_name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.activity_name.length}/100 characters
              </p>
            </div>

            {/* Activity Description */}
            <div>
              <label htmlFor="activity_description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="activity_description"
                name="activity_description"
                value={formData.activity_description}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={loading}
                rows={4}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${errors.activity_description && touched.activity_description
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                  } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                placeholder="Enter activity description (optional)"
                maxLength={500}
              />
              {errors.activity_description && touched.activity_description && (
                <p className="mt-1 text-sm text-red-600">{errors.activity_description}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.activity_description.length}/500 characters
              </p>
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Initial Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The activity will be created with this status
              </p>
            </div>

            {/* Form Info */}
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Information</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• Activity name must be unique and between 3-100 characters</li>
                <li>• Description is optional but recommended for clarity</li>
                <li>• You can change the status later if needed</li>
                <li>• Active activities can have projects assigned to them</li>
              </ul>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={requestClose}
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !formData.activity_name.trim()}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  Create Activity
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};